'use client';
import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Portal, Drawer, Conditional } from 'components/pcc-components';
import { ROOT_PORTALS } from 'constants/targets';
import { returnFocusToNavItem } from '../../utils/common-utils';
import { CatPrimaryNavItem } from 'components/blocks-components';
import { MAIN_NAV_TRACK } from 'constants/analyticsConstants';
import { useSisUrl } from 'hooks/use-sis-url';
import { useNavigationDrawers } from '../navigation-drawers-context';

type SisMenuItemProps = {
  isLoggedIn: boolean;
  isMobile?: boolean;
};

export const SISClient: FC<PropsWithChildren<SisMenuItemProps>> = ({
  isLoggedIn,
  isMobile = false,
  children
}) => {
  const t = useTranslations();

  const text = t('FULL_SIS_LABEL');
  const title = t('CAT_SIS_HOVER');
  const sisNavItemId = 'sisNavItem';

  const {
    sisDrawer: { drawerProps, initiatorProps, isDrawerOpen }
  } = useNavigationDrawers();
  const drawerWasOpen = useRef(false);

  const userSisUrl = useSisUrl(isLoggedIn);

  const navItemProps = {
    href: userSisUrl,
    ...(!isLoggedIn ? initiatorProps : {})
  };

  useEffect(() => {
    if (!isDrawerOpen && drawerWasOpen.current) {
      returnFocusToNavItem(`#${sisNavItemId}`);
      drawerWasOpen.current = false;
    } else if (isDrawerOpen && !drawerWasOpen.current) {
      drawerWasOpen.current = true;
    }
  }, [isDrawerOpen]);

  return (
    <>
      <CatPrimaryNavItem
        data-testid="sisNavItem"
        id={sisNavItemId}
        isCurrent={isDrawerOpen}
        text={text}
        title={title}
        {...navItemProps}
        data-track-event={MAIN_NAV_TRACK.EVENT}
        data-track-attr-nltext={MAIN_NAV_TRACK.SIS_TEXT}
        data-track-attr-context={MAIN_NAV_TRACK.TOP_MENU_CONTEXT}
        style={
          isMobile
            ? {
                borderBottom:
                  '1px solid var(--cat-theme-color-border-strong-hover)'
              }
            : null
        }
      />
      <Conditional test={!isLoggedIn}>
        <Portal id={ROOT_PORTALS}>
          <Drawer {...drawerProps}>{children}</Drawer>
        </Portal>
      </Conditional>
    </>
  );
};
